import type {PropertyDefinitionBase, PropertyValue} from '@github-ui/custom-properties-types'
import {ShieldLockIcon} from '@primer/octicons-react'
import {FormControl, Stack} from '@primer/react'
import {clsx} from 'clsx'
import {forwardRef, type ReactNode, useEffect, useImperativeHandle, useMemo, useRef} from 'react'

import {CustomPropertyInput} from './CustomPropertyInput'
import {type EditTableProps, ResetDropdown} from './CustomPropertyValuesTable'
import styles from './CustomPropertyValuesTableV2.module.css'

interface CustomPropertyValuesTableV2Props extends EditTableProps {
  showSummary: boolean
}

export const CustomPropertyValuesTableV2 = forwardRef((props: CustomPropertyValuesTableV2Props, ref) => {
  const {definitions, propertyValuesMap, showUndo = true, showSummary, fetchAllowedValuesUrl} = props

  const {editableDefinitions} = props as EditTableProps
  const editableDefinitionsSet = useMemo(
    () => new Set((editableDefinitions || []).map((definition: PropertyDefinitionBase) => definition.propertyName)),
    [editableDefinitions],
  )

  const {refsByPropertyName, setRef, clearRefs} = usePropertyInputRefs()

  useEffect(() => {
    clearRefs()
  }, [clearRefs, editableDefinitionsSet])

  useImperativeHandle(ref, () => ({
    focusInput: (propertyName: string) => {
      refsByPropertyName[propertyName]?.focus()
    },
  }))

  if (definitions.length === 0) {
    return null
  }

  return (
    <div>
      {definitions.map(definition => {
        const {propertyName} = definition
        const field = propertyValuesMap[propertyName]
        const isEditable = 'editableDefinitions' in props && editableDefinitionsSet.has(propertyName)

        return (
          <div
            data-testid="property-row"
            key={propertyName}
            className={clsx(styles.propertyRow, showSummary ? styles.readRow : styles.editRow)}
          >
            {showSummary ? (
              <>
                <span className="text-bold fgColor-default mr-2">{`${propertyName}:`}</span>
                <span className="fgColor-muted">{getReadOnlyValue(definition, field?.value)}</span>
              </>
            ) : isEditable ? (
              <CustomPropertyEditValueRowV2
                ref={element => {
                  setRef(propertyName, element)
                }}
                key={propertyName}
                changed={!!field?.changed}
                mixed={!!field?.mixed}
                propertyValue={field?.value}
                definition={definition}
                error={field?.error}
                onChange={newValue => props.setPropertyValue(propertyName, newValue)}
                onReset={() => props.revertPropertyValue(propertyName)}
                showUndo={showUndo}
                fetchAllowedValuesUrl={fetchAllowedValuesUrl}
              />
            ) : (
              <CustomPropertyReadValueRowV2 key={propertyName} propertyValue={field?.value} definition={definition} />
            )}
          </div>
        )
      })}
    </div>
  )
})

CustomPropertyValuesTableV2.displayName = 'CustomPropertyValuesTableV2'

const CustomPropertyEditValueRowV2 = forwardRef(
  (
    {
      propertyValue,
      definition,
      changed,
      mixed = false,
      error,
      onChange,
      onReset,
      showUndo,
      fetchAllowedValuesUrl,
    }: {
      propertyValue?: PropertyValue
      definition: PropertyDefinitionBase
      changed: boolean
      mixed?: boolean
      error?: string
      onChange: (value: PropertyValue) => void
      onReset(): void
      showUndo: boolean
      fetchAllowedValuesUrl?: (propertyName: string) => string
    },
    ref: React.ForwardedRef<HTMLElement>,
  ) => {
    const {propertyName, description} = definition

    const propertyNameValidationId = `${propertyName}-validation-error`

    return (
      <DataTableRow
        label={propertyName}
        description={description}
        value={
          <>
            <div className={styles.controlsContainer}>
              <CustomPropertyInput
                ref={ref}
                {...definition}
                propertyValue={propertyValue}
                mixed={mixed}
                onChange={onChange}
                inputProps={error ? {'aria-describedby': propertyNameValidationId} : {}}
                fetchAllowedValuesUrl={fetchAllowedValuesUrl}
              />
            </div>
            {error && (
              <FormControl.Validation
                variant="error"
                id={propertyNameValidationId}
                className={styles.FormControl_Validation}
              >
                {error}
              </FormControl.Validation>
            )}
          </>
        }
        trailing={<ResetDropdown definition={definition} {...{changed, onReset, onChange, showUndo}} />}
      />
    )
  },
)

CustomPropertyEditValueRowV2.displayName = 'CustomPropertyEditValueRowV2'

function CustomPropertyReadValueRowV2({
  propertyValue,
  definition,
}: {
  propertyValue?: PropertyValue
  definition: PropertyDefinitionBase
}) {
  const {description, propertyName} = definition
  const readOnlyValue = getReadOnlyValue(definition, propertyValue)

  return (
    <DataTableRow
      label={propertyName}
      description={description}
      value={
        <Stack direction="horizontal" gap="condensed">
          <Stack.Item shrink={false}>
            <ShieldLockIcon size="small" />
          </Stack.Item>
          <Stack.Item>
            <span>{readOnlyValue}</span>
          </Stack.Item>
        </Stack>
      }
    />
  )
}

const getCommaSeparatedString = (value: PropertyValue) => (Array.isArray(value) ? value.join(', ') : value)

const getReadOnlyValue = (definition: PropertyDefinitionBase, value?: PropertyValue) => {
  if (value) return value
  if (definition.defaultValue) return `Default (${getCommaSeparatedString(definition.defaultValue)})`
  return ''
}

function DataTableRow({
  label,
  description,
  bottomMessage,
  value,
  trailing,
}: {
  label: string
  description: string | null
  bottomMessage?: ReactNode
  value: ReactNode
  trailing?: ReactNode
}) {
  return (
    <FormControl className={clsx(styles.FormControl, bottomMessage && styles.hasBottomMessage)}>
      <FormControl.Label data-testid="property-name" className={styles.FormControl_Label}>
        {label}
      </FormControl.Label>
      <div className={styles.formInputArea}>{value}</div>
      {trailing ? <div className={styles.actionButtonsArea}>{trailing}</div> : null}
      {description && <FormControl.Caption className={styles.FormControl_Caption}>{description}</FormControl.Caption>}
      {bottomMessage ? <div className={styles.formMessageArea}>{bottomMessage}</div> : null}
    </FormControl>
  )
}

export interface CustomPropertyValuesTableRef {
  focusInput: (propertyName: string) => void
}

const usePropertyInputRefs = () => {
  const refsByName = useRef<Record<string, HTMLElement | null>>({})

  const setRef = (propertyName: string, element: HTMLElement | null) => {
    refsByName.current[propertyName] = element
  }

  const clearRefs = () => {
    refsByName.current = {}
  }

  // eslint-disable-next-line react-hooks/refs
  return {refsByPropertyName: refsByName.current, setRef, clearRefs}
}

try{ CustomPropertyReadValueRowV2.displayName ||= 'CustomPropertyReadValueRowV2' } catch {}
try{ DataTableRow.displayName ||= 'DataTableRow' } catch {}